import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFavoriteAnimation } from '../../hooks/useFavoriteAnimation';
import { Heart } from 'lucide-react';

interface AnimatingHeart {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export function AddToFavoriteAnimation() {
  const { animations, removeAnimation } = useFavoriteAnimation();
  const [animatingHearts, setAnimatingHearts] = useState<AnimatingHeart[]>([]);

  useEffect(() => {
    animations.forEach((anim) => {
      // Find favorites category button
      const target = document.querySelector('[data-favorites-category="true"]');
      if (!target) {
        removeAnimation(anim.id);
        return;
      }

      const targetRect = target.getBoundingClientRect();
      const endX = targetRect.left + targetRect.width / 2;
      const endY = targetRect.top + targetRect.height / 2;

      setAnimatingHearts((prev) => [
        ...prev,
        {
          id: anim.id,
          startX: anim.startX,
          startY: anim.startY,
          endX,
          endY,
        },
      ]);

      // Remove from animations state
      removeAnimation(anim.id);

      // Remove animating heart after animation
      setTimeout(() => {
        setAnimatingHearts((prev) => prev.filter((h) => h.id !== anim.id));
      }, 800);
    });
  }, [animations, removeAnimation]);

  if (animatingHearts.length === 0) return null;

  return createPortal(
    <>
      {animatingHearts.map((heart) => (
        <div
          key={heart.id}
          className="fixed pointer-events-none z-[9999]"
          style={{
            left: heart.startX,
            top: heart.startY,
            transform: 'translate(-50%, -50%)',
            animation: 'flyToFavorite 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
            '--end-x': `${heart.endX - heart.startX}px`,
            '--end-y': `${heart.endY - heart.startY}px`,
          } as React.CSSProperties}
        >
          <Heart className="w-8 h-8 text-red-500 fill-red-500 drop-shadow-lg" />
        </div>
      ))}
      <style>{`
        @keyframes flyToFavorite {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translate(
              calc(-50% + var(--end-x) * 0.5),
              calc(-50% + var(--end-y) * 0.5 - 30px)
            ) scale(1.2);
            opacity: 1;
          }
          100% {
            transform: translate(
              calc(-50% + var(--end-x)),
              calc(-50% + var(--end-y))
            ) scale(0.3);
            opacity: 0;
          }
        }
      `}</style>
    </>,
    document.body
  );
}
